import type { TFunction } from "i18next";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { EmploymentTemplateListItem } from "services/api/company/staff.service";
import { useCompanySubscription } from "web-apps/company/components/common/hooks/useCompanySubscription";
import type { UnitTeamOption } from "web-apps/company/components/common/filters/unitTeamDropdown.component";
import {
  getAgeFromPersonalIdentityNumber,
  isValidPersonalIdentityNumber,
  normalizePersonalIdentityNumber,
} from "web-apps/company/utils/personalIdentityNumber.utils";
import { useCompanyStore } from "web-apps/company/stores/companyStore/companyStore";
import { useToastStore } from "stores/toastStore/toastStore";
import { toastActions } from "stores/toastStore/toastStore.actions";
import {
  getAddStaffSectionCompletion,
  getVisibleAddStaffSections,
  type AddStaffSectionCompletion,
  type AddStaffSectionName,
} from "./addStaffPanel.sections.logic";
import type {
  AddStaffFormData,
  AddStaffOption,
  AddStaffValidationRequirements,
} from "./addStaffPanel.contract";
import {
  applyEmploymentTemplateToAddStaffForm,
  createInitialFormData,
  isAddStaffFormStarted,
  mapAddStaffFormDataToWorkerPayload,
} from "./addStaffPanel.mapper";
import {
  getSalaryAmountLabel,
  getAddStaffOptions,
} from "./addStaffPanel.options.logic";
import {
  createWorkerRequest,
  fetchEmployeeNumberRequest,
  fetchTaxTableRequest,
  fetchEmploymentTemplateDetailsRequest,
  fetchEmploymentTemplatesRequest,
  lookupWorkerByPhoneRequest,
  type EmploymentTemplateDetails,
  type PhoneLookupState,
  type SubmitState,
} from "./addStaffPanel.request.logic";
import { getAddStaffFieldErrors } from "./addStaffPanel.validation.logic";
import {
  getAddStaffValidationRequirements,
  getAddStaffValidationState,
} from "./addStaffPanel.validator";
import {
  toEmploymentTemplateMenuOptions,
  type EmploymentTemplateMenuOption,
} from "../shared/staffEmploymentTemplate.logic";
import {
  toEmploymentUiRules,
  withEmploymentTypeChangePrefill,
  withNormalizedEmploymentFormByRules,
} from "../shared/staffEmploymentRules.logic";
import { hasValidPhoneNumber } from "../shared/staffInputValidation.logic";
import { toApiPhoneNumber } from "utils/phoneNumber.logic";

interface UseAddStaffPanelLogicProps {
  readonly isOpen: boolean;
  readonly t: TFunction;
  readonly unitTeamOptions: UnitTeamOption[];
  readonly onClose: () => void;
  readonly onWorkerCreated?: () => void;
}

interface AddStaffPanelLogicResult {
  readonly advanceVacationDaysError: string;
  readonly agreementError: string;
  readonly areFormFieldsDisabled: boolean;
  readonly autofilledFields: ReadonlySet<keyof AddStaffFormData>;
  readonly emailError: string;
  readonly employeeCategoryOptions: AddStaffOption[];
  readonly employeeNumberError: string;
  readonly employmentOptions: AddStaffOption[];
  readonly employmentTemplateMenuOptions: EmploymentTemplateMenuOption[];
  readonly firstNameError: string;
  readonly formData: AddStaffFormData;
  readonly isCreateDisabled: boolean;
  readonly isFormStarted: boolean;
  readonly isLoadingEmployeeNumber: boolean;
  readonly isLoadingTaxTable: boolean;
  readonly isLoadingEmploymentTemplateMenu: boolean;
  readonly isSubmitting: boolean;
  readonly lastNameError: string;
  readonly maxCareOfChildDaysError: string;
  readonly maxParentalLeaveDaysError: string;
  readonly maxSickLeaveDaysError: string;
  readonly noticeDaysError: string;
  readonly paymentMethodError: string;
  readonly paymentMethodOptions: AddStaffOption[];
  readonly paymentOptions: AddStaffOption[];
  readonly personalIdentityNumberError: string;
  readonly phoneError: string;
  readonly roleError: string;
  readonly salaryAmountLabel: string;
  readonly salaryOptions: AddStaffOption[];
  readonly sectionCompletion: AddStaffSectionCompletion;
  readonly submitError: string;
  readonly teamOptions: AddStaffOption[];
  readonly vacationDaysError: string;
  readonly vacationPayoutOptions: AddStaffOption[];
  readonly validationRequirements: AddStaffValidationRequirements;
  readonly visibleSections: AddStaffSectionName[];
  readonly workPercentageError: string;
  readonly workplaceError: string;
  readonly applyEmploymentTemplateSelection: (selection: {
    salaryId: string;
    templateId: string;
  }) => void;
  readonly fetchEmployeeNumber: () => void;
  readonly onAddWorkerClick: () => void;
  readonly onPersonalIdentityNumberBlur: () => void;
  readonly onPhoneNumberBlur: () => void;
  readonly onPhoneNumberChange: (phoneNumber: string) => void;
  readonly setField: <K extends keyof AddStaffFormData>(
    field: K,
    value: AddStaffFormData[K],
  ) => void;
}

const AUTOFILL_HIGHLIGHT_MS = 1200;

const PHONE_LOOKUP_DEBOUNCE_MS = 400;

const toChangedFormFields = (
  previousFormData: AddStaffFormData,
  nextFormData: AddStaffFormData,
): Set<keyof AddStaffFormData> => {
  const formFields = Object.keys(nextFormData) as (keyof AddStaffFormData)[];
  return new Set(
    formFields.filter(
      (field) => previousFormData[field] !== nextFormData[field],
    ),
  );
};

export const useAddStaffPanelLogic = (
  props: UseAddStaffPanelLogicProps,
): AddStaffPanelLogicResult => {
  const isOpen = props.isOpen;
  const onClose = props.onClose;
  const onWorkerCreated = props.onWorkerCreated;
  const translate = props.t;
  const unitTeamOptions = props.unitTeamOptions;
  const [companyState] = useCompanyStore();
  const [, , toastDispatch] = useToastStore();
  const companyId = companyState.company?.id;
  const initialFormDataRef = useRef<AddStaffFormData | null>(null);
  const [formData, setFormData] = useState<AddStaffFormData>(() => {
    const initialFormData = createInitialFormData();
    initialFormDataRef.current = initialFormData;
    return initialFormData;
  });
  const [isLoadingEmployeeNumber, setIsLoadingEmployeeNumber] = useState(false);
  const [isLoadingTaxTable, setIsLoadingTaxTable] = useState(false);
  const [autofilledFields, setAutofilledFields] = useState<
    ReadonlySet<keyof AddStaffFormData>
  >(new Set());
  const [employmentTemplates, setEmploymentTemplates] = useState<
    EmploymentTemplateListItem[]
  >([]);
  const [isLoadingEmploymentTemplates, setIsLoadingEmploymentTemplates] =
    useState(false);
  const [employmentTemplateDetails, setEmploymentTemplateDetails] =
    useState<EmploymentTemplateDetails>({});
  const [
    isLoadingEmploymentTemplateDetails,
    setIsLoadingEmploymentTemplateDetails,
  ] = useState(false);
  const [phoneLookupState, setPhoneLookupState] = useState<PhoneLookupState>({
    status: "idle",
  });
  const [submitState, setSubmitState] = useState<SubmitState>({
    status: "idle",
  });
  const [submitError, setSubmitError] = useState("");
  const isMountedRef = useRef(true);
  const employeeNumberRequestIdRef = useRef(0);
  const taxTableRequestIdRef = useRef(0);
  const createWorkerRequestIdRef = useRef(0);
  const phoneLookupRequestIdRef = useRef(0);
  const phoneLookupTimeoutRef = useRef<number>();
  const employmentTemplatesRequestIdRef = useRef(0);
  const employmentTemplateDetailsRequestIdRef = useRef(0);
  const companyAddressRef = useRef(companyState.company?.address);
  companyAddressRef.current = companyState.company?.address;

  const cancelPendingPhoneLookup = useCallback((): void => {
    if (phoneLookupTimeoutRef.current == null) return;
    window.clearTimeout(phoneLookupTimeoutRef.current);
    phoneLookupTimeoutRef.current = undefined;
  }, []);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      cancelPendingPhoneLookup();
    };
  }, [cancelPendingPhoneLookup]);

  /**
   * Flags fields the user did not type into themselves, so the form can point
   * out what a template or a phone lookup changed.
   */
  const markAutofilledFields = useCallback(
    (fields: Set<keyof AddStaffFormData>): void => {
      if (fields.size === 0) return;
      setAutofilledFields(fields);
    },
    [],
  );

  useEffect(() => {
    if (autofilledFields.size === 0) return;
    const timeout = window.setTimeout(
      () => setAutofilledFields(new Set()),
      AUTOFILL_HIGHLIGHT_MS,
    );
    return () => window.clearTimeout(timeout);
  }, [autofilledFields]);

  const setField = useCallback(
    <K extends keyof AddStaffFormData>(
      field: K,
      value: AddStaffFormData[K],
    ): void => {
      setFormData((previousFormData) =>
        withEmploymentTypeChangePrefill({
          changedField: field,
          changedValue: value,
          companyAddress: companyAddressRef.current,
          form: { ...previousFormData, [field]: value },
        }),
      );
      setSubmitError("");
    },
    [],
  );

  const fetchEmployeeNumber = useCallback((): void => {
    if (!companyId) return;
    const requestId = employeeNumberRequestIdRef.current + 1;
    employeeNumberRequestIdRef.current = requestId;
    setIsLoadingEmployeeNumber(true);
    void fetchEmployeeNumberRequest({
      companyId,
      requestId,
      getCurrentRequestId: () => employeeNumberRequestIdRef.current,
      isMountedRef,
      setField,
      setIsLoadingEmployeeNumber,
    });
  }, [companyId, setField]);

  /**
   * Leaving the personal number says it is finished, which is the first moment
   * the tax table can be looked up for it.
   */
  const onPersonalIdentityNumberBlur = useCallback((): void => {
    if (!companyId) return;
    if (!isValidPersonalIdentityNumber(formData.personalIdentityNumber)) return;
    const personalNumber = normalizePersonalIdentityNumber(
      formData.personalIdentityNumber,
    );

    const requestId = taxTableRequestIdRef.current + 1;
    taxTableRequestIdRef.current = requestId;
    setIsLoadingTaxTable(true);
    void fetchTaxTableRequest({
      companyId,
      personalNumber,
      requestId,
      getCurrentRequestId: () => taxTableRequestIdRef.current,
      isMountedRef,
      setField,
      setIsLoadingTaxTable,
    });
  }, [companyId, formData.personalIdentityNumber, setField]);

  const fetchEmploymentTemplates = useCallback((): void => {
    if (!companyId) return;
    const requestId = employmentTemplatesRequestIdRef.current + 1;
    employmentTemplatesRequestIdRef.current = requestId;
    setIsLoadingEmploymentTemplates(true);
    void fetchEmploymentTemplatesRequest({
      companyId,
      requestId,
      getCurrentRequestId: () => employmentTemplatesRequestIdRef.current,
      isMountedRef,
      setEmploymentTemplates,
      setIsLoadingEmploymentTemplates,
    });
  }, [companyId]);

  const lookupPhoneNumber = useCallback(
    (phoneNumber: string): void => {
      if (!companyId) return;
      setSubmitState({ status: "idle" });
      setSubmitError("");
      setPhoneLookupState({ status: "checking" });
      const requestId = phoneLookupRequestIdRef.current + 1;
      phoneLookupRequestIdRef.current = requestId;
      void lookupWorkerByPhoneRequest({
        companyId,
        phoneNumber,
        requestId,
        getCurrentRequestId: () => phoneLookupRequestIdRef.current,
        isMountedRef,
        markAutofilledFields,
        setFormData,
        setPhoneLookupState,
        translate,
      });
    },
    [companyId, markAutofilledFields, translate],
  );

  const onPhoneNumberChange = useCallback(
    (phoneNumber: string): void => {
      phoneLookupRequestIdRef.current += 1;
      cancelPendingPhoneLookup();
      setPhoneLookupState({ status: "idle" });
      setSubmitState({ status: "idle" });
      setSubmitError("");
      setField("phoneNumber", phoneNumber);

      if (!hasValidPhoneNumber({ value: phoneNumber })) return;
      phoneLookupTimeoutRef.current = window.setTimeout(() => {
        phoneLookupTimeoutRef.current = undefined;
        lookupPhoneNumber(phoneNumber);
      }, PHONE_LOOKUP_DEBOUNCE_MS);
    },
    [cancelPendingPhoneLookup, lookupPhoneNumber, setField],
  );

  const onPhoneNumberBlur = useCallback((): void => {
    if (!hasValidPhoneNumber({ value: formData.phoneNumber })) {
      cancelPendingPhoneLookup();
      if (formData.phoneNumber.trim().length === 0) {
        setPhoneLookupState({ status: "idle" });
        return;
      }
      setPhoneLookupState({
        status: "invalid",
        message: translate("Staff.AddStaffPanelPhoneNumberInvalid"),
      });
      return;
    }
    // Leaving the field says the number is finished, so run the lookup now
    // rather than making the user wait out the rest of the debounce.
    if (phoneLookupTimeoutRef.current != null) {
      cancelPendingPhoneLookup();
      lookupPhoneNumber(formData.phoneNumber);
      return;
    }
    if (phoneLookupState.status === "invalid") {
      setPhoneLookupState({ status: "idle" });
    }
  }, [
    cancelPendingPhoneLookup,
    formData.phoneNumber,
    lookupPhoneNumber,
    phoneLookupState.status,
    translate,
  ]);

  const loadEmploymentTemplateDetails = useCallback((): void => {
    if (!companyId) return;
    if (employmentTemplates.length === 0) return;
    const requestId = employmentTemplateDetailsRequestIdRef.current + 1;
    employmentTemplateDetailsRequestIdRef.current = requestId;
    setIsLoadingEmploymentTemplateDetails(true);
    void fetchEmploymentTemplateDetailsRequest({
      companyId,
      employmentTemplateIds: employmentTemplates.map((template) => template.id),
      requestId,
      getCurrentRequestId: () => employmentTemplateDetailsRequestIdRef.current,
      isMountedRef,
      setEmploymentTemplateDetails,
      setIsLoadingEmploymentTemplateDetails,
    });
  }, [companyId, employmentTemplates]);

  const applyEmploymentTemplateSelection = useCallback(
    (selection: { salaryId: string; templateId: string }): void => {
      const template = employmentTemplateDetails[selection.templateId];
      if (template == null) return;

      const selectedSalary =
        selection.salaryId.length > 0
          ? template.salaries?.find(
              (salary) => String(salary.id) === selection.salaryId,
            )
          : undefined;
      const templateFormData = applyEmploymentTemplateToAddStaffForm(
        formData,
        template,
      );
      const nextFormData =
        selectedSalary == null
          ? templateFormData
          : {
              ...templateFormData,
              salaryAmount: String(selectedSalary.value),
            };

      setFormData(nextFormData);
      setSubmitError("");
      markAutofilledFields(toChangedFormFields(formData, nextFormData));
    },
    [employmentTemplateDetails, formData, markAutofilledFields],
  );

  useEffect(() => {
    createWorkerRequestIdRef.current += 1;
    employeeNumberRequestIdRef.current += 1;
    taxTableRequestIdRef.current += 1;
    phoneLookupRequestIdRef.current += 1;
    employmentTemplatesRequestIdRef.current += 1;
    employmentTemplateDetailsRequestIdRef.current += 1;
    cancelPendingPhoneLookup();
    if (!isOpen) return;

    const initialFormData = createInitialFormData();
    initialFormDataRef.current = initialFormData;
    setFormData(initialFormData);
    setPhoneLookupState({ status: "idle" });
    setSubmitState({ status: "idle" });
    setSubmitError("");
    setEmploymentTemplates([]);
    setEmploymentTemplateDetails({});
    setIsLoadingEmploymentTemplateDetails(false);
    setAutofilledFields(new Set());
    fetchEmployeeNumber();
    fetchEmploymentTemplates();
  }, [
    cancelPendingPhoneLookup,
    fetchEmployeeNumber,
    fetchEmploymentTemplates,
    isOpen,
  ]);

  useEffect(() => {
    if (!isOpen) return;
    loadEmploymentTemplateDetails();
  }, [isOpen, loadEmploymentTemplateDetails]);

  const subscription = useCompanySubscription({
    companyId,
    isEnabled: isOpen,
  });
  const employmentRules = useMemo(() => {
    return toEmploymentUiRules({
      companySettings: companyState.company?.companySettings ?? [],
      employmentType: formData.employmentType ?? "",
      salaryType: formData.salaryType ?? "",
      subscription,
    });
  }, [
    companyState.company?.companySettings,
    formData.employmentType,
    formData.salaryType,
    subscription,
  ]);

  useEffect(() => {
    setFormData((previousFormData) => {
      const current = {
        salaryType: previousFormData.salaryType ?? "",
        employeeCategory: previousFormData.employeeCategory,
        paymentType: previousFormData.paymentType,
      };
      const normalized = withNormalizedEmploymentFormByRules(
        current,
        employmentRules,
      );
      return normalized.salaryType === current.salaryType &&
        normalized.employeeCategory === current.employeeCategory &&
        normalized.paymentType === current.paymentType
        ? previousFormData
        : { ...previousFormData, ...normalized };
    });
  }, [employmentRules]);

  const options = useMemo(() => {
    return getAddStaffOptions({
      employmentRules,
      translate,
      unitTeamOptions,
    });
  }, [employmentRules, translate, unitTeamOptions]);
  const workerAge = useMemo(() => {
    return getAgeFromPersonalIdentityNumber(formData.personalIdentityNumber);
  }, [formData.personalIdentityNumber]);
  const employmentTemplateMenuOptions = useMemo(() => {
    return toEmploymentTemplateMenuOptions({
      templateDetails: employmentTemplateDetails,
      templates: employmentTemplates,
      workerAge,
    });
  }, [employmentTemplateDetails, employmentTemplates, workerAge]);
  const validationRequirements = useMemo(() => {
    return getAddStaffValidationRequirements(formData);
  }, [formData]);
  const validationState = useMemo(() => {
    return getAddStaffValidationState({
      canSelectSimflexityEmployee: employmentRules.canSelectSimflexityEmployee,
      canSelectSimflexityPay: employmentRules.canSelectSimflexityPay,
      formData,
      requirements: validationRequirements,
    });
  }, [
    employmentRules.canSelectSimflexityEmployee,
    employmentRules.canSelectSimflexityPay,
    formData,
    validationRequirements,
  ]);
  const fieldErrors = useMemo(() => {
    return getAddStaffFieldErrors({
      formData,
      translate,
      validationState,
    });
  }, [formData, translate, validationState]);
  const salaryAmountLabel = useMemo(() => {
    return getSalaryAmountLabel({
      salaryType: formData.salaryType,
      translate,
    });
  }, [formData.salaryType, translate]);
  const isSubmitting = submitState.status === "submitting";
  const hasCheckedPhoneNumber = phoneLookupState.status === "verified";
  const phoneError =
    phoneLookupState.status === "invalid" ||
    phoneLookupState.status === "alreadyExists"
      ? phoneLookupState.message
      : "";
  const areFormFieldsDisabled =
    formData.phoneNumber.trim().length === 0 || !hasCheckedPhoneNumber;
  const visibleSections = getVisibleAddStaffSections({
    employmentType: formData.employmentType,
    salaryType: formData.salaryType,
  });
  const sectionCompletion = getAddStaffSectionCompletion({
    formData,
    requirements: validationRequirements,
  });
  const isCreateDisabled =
    areFormFieldsDisabled ||
    isSubmitting ||
    !validationState.hasAllRequiredValues;
  const isFormStarted = useMemo(() => {
    return (
      initialFormDataRef.current != null &&
      isAddStaffFormStarted(formData, initialFormDataRef.current)
    );
  }, [formData]);

  const onAddWorkerClick = useCallback((): void => {
    if (!companyId || isSubmitting) return;
    if (!validationState.hasAllRequiredValues) return;
    const apiPhoneNumber = toApiPhoneNumber(formData.phoneNumber);
    if (!apiPhoneNumber.succeeded) {
      setPhoneLookupState({
        status: "invalid",
        message: translate("Staff.AddStaffPanelPhoneNumberInvalid"),
      });
      return;
    }
    setSubmitError("");
    setSubmitState({ status: "submitting" });
    const requestId = createWorkerRequestIdRef.current + 1;
    createWorkerRequestIdRef.current = requestId;
    void createWorkerRequest({
      companyId,
      formData,
      requestId,
      payload: mapAddStaffFormDataToWorkerPayload(
        formData,
        apiPhoneNumber.data,
      ),
      getCurrentRequestId: () => createWorkerRequestIdRef.current,
      isMountedRef,
      onWorkerCreated: () => {
        toastActions(toastDispatch).showToast({
          variant: "success",
          message: translate("Staff.AddStaffPanelWorkerCreated", {
            name: `${formData.firstName} ${formData.lastName}`.trim(),
          }),
        });
        onWorkerCreated?.();
      },
      onClose,
      setSubmitError,
      setSubmitState,
      translate,
    });
  }, [
    validationState.hasAllRequiredValues,
    companyId,
    formData,
    isSubmitting,
    onClose,
    onWorkerCreated,
    toastDispatch,
    translate,
  ]);

  return {
    advanceVacationDaysError: fieldErrors.advanceVacationDays,
    agreementError: fieldErrors.agreement,
    areFormFieldsDisabled,
    autofilledFields,
    emailError: fieldErrors.email,
    employeeCategoryOptions: options.employeeCategoryOptions,
    employeeNumberError: fieldErrors.employeeNumber,
    employmentOptions: options.employmentOptions,
    employmentTemplateMenuOptions,
    firstNameError: fieldErrors.firstName,
    formData,
    isCreateDisabled,
    isFormStarted,
    isLoadingEmployeeNumber,
    isLoadingTaxTable,
    isLoadingEmploymentTemplateMenu:
      isLoadingEmploymentTemplates || isLoadingEmploymentTemplateDetails,
    isSubmitting,
    lastNameError: fieldErrors.lastName,
    maxCareOfChildDaysError: fieldErrors.maxCareOfChildDays,
    maxParentalLeaveDaysError: fieldErrors.maxParentalLeaveDays,
    maxSickLeaveDaysError: fieldErrors.maxSickLeaveDays,
    noticeDaysError: fieldErrors.noticeDays,
    paymentMethodError: fieldErrors.paymentMethod,
    paymentMethodOptions: options.paymentMethodOptions,
    paymentOptions: options.paymentOptions,
    personalIdentityNumberError: fieldErrors.personalIdentityNumber,
    phoneError,
    roleError: fieldErrors.role,
    salaryAmountLabel,
    salaryOptions: options.salaryOptions,
    sectionCompletion,
    submitError,
    teamOptions: options.teamOptions,
    vacationDaysError: fieldErrors.vacationDays,
    vacationPayoutOptions: options.vacationPayoutOptions,
    validationRequirements,
    visibleSections,
    workPercentageError: fieldErrors.workPercentage,
    workplaceError: fieldErrors.workplace,
    applyEmploymentTemplateSelection,
    fetchEmployeeNumber,
    onAddWorkerClick,
    onPersonalIdentityNumberBlur,
    onPhoneNumberBlur,
    onPhoneNumberChange,
    setField,
  };
};
